import { Button, Card, Divider, Modal, Typography } from 'antd';

import { useAdminCompanyController_updateOneById } from '@api-client/generated/AdminCompanyController/updateOneById';
import { useAdminIncorporationController_approveKyc } from '@api-client/generated/AdminIncorporationController/approveKyc';
import { useAdminIncorporationController_failKyc } from '@api-client/generated/AdminIncorporationController/failKyc';
import { useAdminIncorporationController_findOneById } from '@api-client/generated/AdminIncorporationController/findOneById';
import { useAdminIncorporationController_updateOneById } from '@api-client/generated/AdminIncorporationController/updateOneById';
import { useAdminPersonsController_findAll } from '@api-client/generated/AdminPersonsController/findAll';
import { Schemas } from '@api-client/generated/types';
import { Loader } from '@components';
import { CompanyDetails } from '@entities/onboarding/sections/CompanyDetails';
import { CompanyOwners } from '@entities/onboarding/sections/CompanyOwners';
import { CompanyStructure } from '@entities/onboarding/sections/CompanyStructure';
import { useCurrentCompanyId } from '@hooks';

import * as S from './styled';

const { Title } = Typography;

type IncorporationDto = Schemas.AdminIncorporationDto;

const Incorporation = () => {
  const [modal, contextHolder] = Modal.useModal();

  const companyId = useCurrentCompanyId();

  const {
    data,
    isPending: isLoading,
    refetch: refetchIncorporation,
  } = useAdminIncorporationController_findOneById({
    params: {
      companyId,
    },
  });

  const { data: persons = [], isPending: isLoadingPersons } =
    useAdminPersonsController_findAll({
      params: { companyId },
    });

  const { mutateAsync: updateIncorporation } =
    useAdminIncorporationController_updateOneById();
  const { mutateAsync: updateCompany } =
    useAdminCompanyController_updateOneById();
  const { mutateAsync: approveKYC } =
    useAdminIncorporationController_approveKyc();
  const { mutateAsync: failKYC } = useAdminIncorporationController_failKyc();

  const handleUpdatedIncorporation = async (
    body: IncorporationDto,
    {
      title,
      content,
    }: {
      title?: string;
      content?: string;
    } = {}
  ) => {
    const isConfirmed = await modal.confirm({
      title: title || 'Are you sure?',
      content,
    });

    if (isConfirmed) {
      await updateIncorporation({
        parameter: { companyId },
        requestBody: body,
      });

      await refetchIncorporation();
    }
  };

  const handleNotifyFailedKYC = async () => {
    const isConfirmed = await modal.confirm({
      title: 'Are you sure?',
      content: 'We will send an email to the client after you click "Ok".',
    });

    if (isConfirmed) {
      await failKYC({ parameter: { companyId } });
      await refetchIncorporation();
    }
  };

  const handleConfirmKYC = async () => {
    const isConfirmed = await modal.confirm({
      title: 'Are you sure?',
      content: 'It will just mark that you approved the KYC documents',
    });

    if (isConfirmed) {
      await approveKYC({ parameter: { companyId } });
      await refetchIncorporation();
    }
  };

  const handleMarkAsIncorporated = async () => {
    await handleUpdatedIncorporation({
      isShareCapitalConfirmed: true,
      group: 'rcs',
    });

    const status =
      data?.incorporationType === 'incorporation'
        ? 'incorporated'
        : 'in_service';

    await updateCompany({
      parameter: { id: companyId },
      requestBody: { status },
    });
  };

  if (isLoading || !data) {
    return <Loader />;
  }

  return (
    <S.Container gap={24} vertical>
      {contextHolder}

      <Title level={2}>Incorporation Data</Title>

      <Card title="Company Details">
        <CompanyDetails data={data} />
      </Card>

      <Card title="Persons">
        <CompanyStructure persons={persons} isLoading={isLoadingPersons} />
      </Card>

      <Card title="KYC">
        <CompanyOwners
          companyId={companyId}
          persons={persons}
          isLoading={isLoadingPersons}
        />

        <Divider />

        <S.KYCActions align="center" justify="flex-end" gap={16}>
          <Button type="link" onClick={handleNotifyFailedKYC}>
            Notify client of failed KYC
          </Button>

          <Button type="primary" size="large" onClick={handleConfirmKYC}>
            Approve KYC from your side
          </Button>
        </S.KYCActions>
      </Card>

      <S.ToolbarActions align="center" gap={16}>
        {!data.isCheckComplete && (
          <Button
            type="primary"
            onClick={() =>
              handleUpdatedIncorporation(
                { isCheckComplete: true },
                {
                  content:
                    'It will send an email to client and move his status further',
                }
              )
            }
            size="large"
          >
            KYC is passed
          </Button>
        )}
        {!data.isAgreementSigned && (
          <Button
            type="primary"
            onClick={() =>
              handleUpdatedIncorporation({
                isAgreementSigned: true,
                group: 'payment',
              })
            }
            size="large"
          >
            All parties signed
          </Button>
        )}
        {!data.isPaid && (
          <Button
            type="primary"
            size="large"
            onClick={() => handleUpdatedIncorporation({ isPaid: true })}
          >
            Mark as paid
          </Button>
        )}
        {data.companyType !== 'sarl-s' && !data.isShareCapitalConfirmed && (
          <Button
            type="primary"
            size="large"
            onClick={() =>
              handleUpdatedIncorporation({
                isShareCapitalConfirmed: true,
                group: 'rcs',
              })
            }
          >
            Share capital confirmed
          </Button>
        )}
        {!data.isIncorporated && (
          <Button
            type="primary"
            size="large"
            onClick={handleMarkAsIncorporated}
          >
            {data.incorporationType === 'incorporation'
              ? 'Incorporated'
              : 'Enrolled'}
          </Button>
        )}
      </S.ToolbarActions>
    </S.Container>
  );
};

export default Incorporation;
